// --- FINAL, COMPLETE & CORRECTED FILE: packages/ui/src/TextareaField.tsx ---

import React, { useId, TextareaHTMLAttributes, KeyboardEvent, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { HelpCircle } from 'lucide-react';

export interface TextareaFieldProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  labelKey?: string;
  placeholder?: string;
  placeholderKey?: string;
  descriptionText?: string;
  descriptionTextKey?: string;
  errorText?: string;
  errorTextKey?: string;
  textareaClassName?: string;
  labelClassName?: string;
  containerClassName?: string;
  showCharacterCount?: boolean;
  applyCustomScrollbar?: boolean;
  tooltip?: string;
  /** Determines the visual style of the textarea. 'ghost' is borderless and transparent at rest. */
  variant?: 'default' | 'ghost';
  /** If true, the textarea will automatically grow and shrink to fit its content. */
  autoGrow?: boolean;
}

export const TextareaField = React.forwardRef<HTMLTextAreaElement, TextareaFieldProps>(
  ({
    label,
    labelKey,
    placeholder: directPlaceholder,
    placeholderKey,
    descriptionText,
    descriptionTextKey,
    errorText,
    errorTextKey,
    textareaClassName,
    labelClassName,
    containerClassName,
    id,
    disabled,
    rows = 3,
    value,
    maxLength,
    showCharacterCount = false,
    applyCustomScrollbar = false,
    style,
    tooltip,
    variant = 'default',
    autoGrow = false,
    onKeyDown,
    ...props
  }, ref) => {
    const { t } = useTranslation();
    const autoId = useId();
    const fieldId = id || autoId;
    const localRef = useRef<HTMLTextAreaElement>(null);

    useEffect(() => {
      const textarea = localRef.current;
      if (autoGrow && textarea) {
        textarea.style.height = 'auto';
        textarea.style.height = `${textarea.scrollHeight}px`;
      }
    }, [value, autoGrow, rows]);

    const setRefs = (node: HTMLTextAreaElement) => {
      (localRef as React.MutableRefObject<HTMLTextAreaElement | null>).current = node;
      if (typeof ref === 'function') {
        ref(node);
      } else if (ref) {
        (ref as React.MutableRefObject<HTMLTextAreaElement | null>).current = node;
      }
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
      if (onKeyDown) onKeyDown(event);
    };

    const resolvedLabel = labelKey ? t(labelKey) : label;
    const resolvedPlaceholder = placeholderKey ? t(placeholderKey) : directPlaceholder;
    const resolvedDescriptionText = descriptionTextKey ? t(descriptionTextKey) : descriptionText;
    const resolvedErrorText = errorTextKey ? t(errorTextKey) : errorText;
    const hasError = !!resolvedErrorText;
    const currentValueLength = String(value || '').length;

    // --- REFINED NEOBRUTALIST EFFECT ---
    // The translate distance (`-translate-x-0.5` = -2px) now perfectly matches the shadow offset (`2px`).
    // Both effects are applied to the textarea itself on group-hover/group-focus-within for a cohesive "pop".
    const popAndShadowEffect = 'group-hover:-translate-x-0.5 group-hover:-translate-y-0.5 group-focus-within:-translate-x-0.5 group-focus-within:-translate-y-0.5 group-hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,0.9)] group-focus-within:shadow-[2px_2px_0px_0px_rgba(0,0,0,0.9)]';
    const errorPopAndShadowEffect = 'group-hover:-translate-x-0.5 group-hover:-translate-y-0.5 group-focus-within:-translate-x-0.5 group-focus-within:-translate-y-0.5 group-hover:shadow-[2px_2px_0px_0px_rgba(220,38,38,0.9)] group-focus-within:shadow-[2px_2px_0px_0px_rgba(220,38,38,0.9)]';

    const scrollbarClasses = applyCustomScrollbar ? 'scrollbar scrollbar-thin scrollbar-thumb-slate-500 scrollbar-track-slate-100 scrollbar-thumb-rounded-full scrollbar-track-rounded-full' : '';

    return (
      <div className={clsx('w-full', containerClassName)}>
        {resolvedLabel && (
          <div className="flex justify-between items-center mb-1.5">
            <div className="flex items-center">
              <label htmlFor={fieldId} className={clsx(
                'block text-sm font-medium',
                // Neobrutalism
                'text-slate-700',
                // Liquid glass
                '[.theme-liquid-glass_&]:text-white/90',
                labelClassName
              )}>
                {resolvedLabel}
              </label>
              {tooltip && (
                <div className="relative flex items-center ml-1.5 group/tooltip">
                  <HelpCircle className="h-4 w-4 text-slate-400 cursor-help [.theme-liquid-glass_&]:text-white/50" />
                  <div className={clsx('absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-max max-w-xs p-2', 'text-xs text-white bg-slate-800 rounded-md shadow-lg', '[.theme-liquid-glass_&]:bg-slate-800/90 [.theme-liquid-glass_&]:backdrop-blur-xl [.theme-liquid-glass_&]:border [.theme-liquid-glass_&]:border-white/20', 'opacity-0 group-hover/tooltip:opacity-100 transition-opacity pointer-events-none')} role="tooltip">
                    {tooltip}
                    <div className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-x-4 border-x-transparent border-t-4 border-t-slate-800 [.theme-liquid-glass_&]:border-t-slate-800/90"></div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* The parent div provides the `group` context for `group-focus-within` to work correctly */}
        <div className="relative group">
          <textarea
            ref={setRefs}
            id={fieldId}
            rows={rows}
            disabled={disabled}
            placeholder={resolvedPlaceholder}
            value={value}
            maxLength={maxLength}
            style={style}
            onKeyDown={handleKeyDown}
            className={clsx(
              // --- BASE STYLES APPLIED TO ALL VARIANTS ---
              'block w-full rounded-lg border-2 p-3 text-slate-900 placeholder:text-slate-400',
              'focus:outline-none transition-all duration-150 ease-in-out',
              autoGrow ? 'resize-none' : 'resize-y',

              // --- VARIANT STYLES ---
              {
                'default': 'bg-white border-black [.theme-liquid-glass_&]:bg-white/5 [.theme-liquid-glass_&]:border-white/20 [.theme-liquid-glass_&]:text-white [.theme-liquid-glass_&]:placeholder:text-white/40',
                'ghost': 'bg-transparent border-transparent hover:border-black hover:bg-slate-50 focus:border-black [.theme-liquid-glass_&]:hover:border-white/20 [.theme-liquid-glass_&]:hover:bg-white/10 [.theme-liquid-glass_&]:focus:border-white/30 [.theme-liquid-glass_&]:text-white [.theme-liquid-glass_&]:placeholder:text-white/40'
              }[variant],

              // --- DYNAMIC STATE STYLES ---
              // Apply the correct "pop" effect if not disabled
              !disabled && (hasError ? errorPopAndShadowEffect : popAndShadowEffect),
              // Error state styles (primarily for text/placeholder color if border is already handled by ghost:hover)
              hasError && 'border-red-500 text-red-700 placeholder:text-red-400',
              // Disabled state overrides everything
              disabled && 'cursor-not-allowed bg-slate-100 opacity-70 border-slate-300',

              // --- EXTRA STYLES ---
              scrollbarClasses,
              textareaClassName
            )}
            aria-invalid={hasError ? 'true' : 'false'}
            aria-describedby={resolvedErrorText ? `${fieldId}-error` : resolvedDescriptionText ? `${fieldId}-description` : undefined}
            {...props}
          />
        </div>

        {/* --- HELPER TEXT CONTAINER --- */}
        {(resolvedErrorText || (resolvedDescriptionText && !hasError) || showCharacterCount) && (
          <div className="mt-1.5 flex justify-between items-start text-xs">
            <div className="flex-grow pr-2">
              {resolvedErrorText && (
                <p id={`${fieldId}-error`} className="text-red-600 [.theme-liquid-glass_&]:text-rose-400">
                  {resolvedErrorText}
                </p>
              )}
              {resolvedDescriptionText && !hasError && (
                <p id={`${fieldId}-description`} className="text-slate-500 [.theme-liquid-glass_&]:text-white/50">
                  {resolvedDescriptionText}
                </p>
              )}
            </div>
            {showCharacterCount && (
              <p className="ml-2 text-slate-500 shrink-0 [.theme-liquid-glass_&]:text-white/50">
                {currentValueLength}{maxLength !== undefined && ` / ${maxLength}`}
              </p>
            )}
          </div>
        )}
      </div>
    );
  }
);

TextareaField.displayName = 'TextareaField';
